import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Loader2, PiggyBank, ChevronRight } from 'lucide-react';
import { projectFinancialsApi } from '../api';
import { FinancialSummaryView, HEALTH_META } from '../components/FinancialSummaryView';
import { FinancialPie } from '../components/FinancialPie';
import { centsToDollarString } from '../constants';

// Global Financials: same simplified layout, aggregated across
// completed or financially closed projects (authoritative final data only).
export default function OpsFinancials() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    projectFinancialsApi
      .completedSummary()
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="ops-financials">
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white">Financials</h2>
        <p className="text-gray-400 mt-1">
          Company results from completed & financially closed projects. Active jobs are never mixed in.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-500" data-testid="financials-page-loading">
          <Loader2 size={26} className="mx-auto mb-2 animate-spin text-[#D4AF37]" /> Loading…
        </div>
      ) : !data || data.project_count === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="financials-empty">
          <PiggyBank size={36} className="mx-auto mb-3 text-[#D4AF37]" />
          <p className="text-white font-medium">No completed projects yet</p>
          <p className="text-gray-500 text-sm mt-1">
            Once a project is completed or financially closed, its final results roll up here.
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <FinancialSummaryView
              fin={{
                ...data,
                potential_gross_profit_cents: data.gross_profit_cents,
                approved_change_order_count: null,
              }}
              titleNote={`Totals across ${data.project_count} completed project${data.project_count === 1 ? '' : 's'}${data.projects_missing_budget_count ? ` · ${data.projects_missing_budget_count} missing a customer budget` : ''}`}
            />
            <FinancialPie
              materialsNet={(data.materials_misc_cents || 0) - (data.reimbursements_cents || 0)}
              employees={data.employee_costs_cents}
              subs={data.subcontractor_costs_cents}
              profit={data.gross_profit_cents}
            />
          </div>

          <Card className="bg-[#111111] border-gray-800 overflow-hidden" data-testid="financials-project-list">
            <div className="px-4 py-3 border-b border-gray-800">
              <h4 className="text-sm font-semibold text-white">Completed Projects</h4>
            </div>
            <div className="divide-y divide-gray-800/70">
              {data.projects.map((f) => {
                const h = HEALTH_META[f.budget_health] || HEALTH_META.no_budget;
                return (
                  <button
                    key={f.project.id}
                    onClick={() => navigate(`/ops/projects/${f.project.id}?tab=financials`)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#1A1A1A] transition-colors"
                    data-testid={`financials-project-row-${f.project.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-white font-medium truncate">{f.project.name}</div>
                      <div className="text-[11px] text-gray-600 mt-0.5">
                        Costs {centsToDollarString(f.total_costs_cents)}
                        {f.revenue_basis_cents != null ? ` of ${centsToDollarString(f.revenue_basis_cents)} budget` : ' · no budget set'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-[11px] border rounded-full px-2 py-0.5 ${h.cls}`}>{h.label}</span>
                      <span className={`text-sm font-semibold ${f.potential_gross_profit_cents == null ? 'text-gray-500' : f.potential_gross_profit_cents >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {f.potential_gross_profit_cents == null ? '—' : centsToDollarString(f.potential_gross_profit_cents)}
                      </span>
                      <ChevronRight size={14} className="text-gray-600" />
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
